import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, CircularProgress, IconButton, Snackbar, SnackbarContent, Stack, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { LoadFailure } from '../../components/LoadFailure';
import { PAGE_MAX_WIDTH } from '../../theme';
import { Sidebar } from '../Home/Sidebar';
import { SIDEBAR_SECTIONS } from '../Home/sidebarSections';
import { COLORS } from '../IndividualPlan/tokens';
import { ROSTER } from '../Roster/tokens';
import { PRIMARY_BUTTON_SX, TEXT_BUTTON_SX } from '../Samples/SampleBanner';
import { answerMatch, clearMatchAnswer, fetchMatches, MatchesRefreshError, type MatchAnswerKind, type MatchRecord, type MatchesState, type PossibleMatch } from './matchesApi';
import {
  answerToast,
  categoryOf,
  diffSegments,
  displayDob,
  groupOpenMatches,
  heldOutShort,
  matchName,
  nameSourceLabel,
  openMatches,
  recordId,
  recordName,
  reviewedMatches,
  sourceLabel,
  visibleMatches,
  type DiffSegment,
} from './matchRows';
import { DATA_REVIEW } from './dataReviewTokens';
import { HEIGHT_BELOW_BARS } from '../../core/bannerOffset';

const TOAST_BG = '#2C2C52';
const CARD_SX = {
  bgcolor: '#FFFFFF',
  borderRadius: 2,
  border: `1px solid ${COLORS.hairlineSoft}`,
  boxShadow: '0 1px 2px rgba(16, 24, 40, 0.04)',
  p: { xs: 2.5, md: 3 },
} as const;

type Toast = { id: string; message: string };

function Marked({ text, segments }: { text: string; segments?: DiffSegment[] }) {
  if (!segments) return text;
  return (
    <>
      {segments.map((segment, idx) =>
        segment.diff ? (
          <Box key={idx} component="mark" data-testid="diff-mark" sx={{ bgcolor: DATA_REVIEW.diffMark, color: 'inherit', borderRadius: '2px', px: '1px' }}>
            {segment.text}
          </Box>
        ) : (
          <span key={idx}>{segment.text}</span>
        ),
      )}
    </>
  );
}

function RecordColumn({ record, records, name, id, dob }: { record: MatchRecord; records: MatchRecord[]; name?: DiffSegment[]; id?: DiffSegment[]; dob?: DiffSegment[] }) {
  const studentId = recordId(record, records);
  const nameSource = nameSourceLabel(record);
  return (
    <Box data-testid="match-record" sx={{ flex: '1 1 220px', minWidth: 0 }}>
      <Typography sx={{ fontSize: 12, lineHeight: '16px', color: COLORS.muted, mb: '2px' }}>{sourceLabel(record)}</Typography>
      <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ gap: 1 }}>
        <Typography component="b" sx={{ fontSize: 15, lineHeight: '22px', fontWeight: 700, color: COLORS.ink }}>
          <Marked text={recordName(record)} segments={name} />
        </Typography>
        {studentId ? (
          <Typography component="span" sx={{ fontSize: 14, lineHeight: '20px', color: COLORS.muted }}>
            <Marked text={studentId} segments={id} />
          </Typography>
        ) : (
          <Box component="span" sx={{ fontSize: 12, lineHeight: '18px', px: 0.75, borderRadius: 1, bgcolor: DATA_REVIEW.stripBg, color: DATA_REVIEW.stripFg, fontWeight: 700 }}>
            No ID
          </Box>
        )}
        {record.dob && (
          <Typography component="span" sx={{ fontSize: 14, lineHeight: '20px', color: COLORS.muted }}>
            DOB <Marked text={displayDob(record.dob)} segments={dob} />
          </Typography>
        )}
      </Stack>
      {nameSource && (
        <Typography data-testid="match-name-source" sx={{ fontSize: 12, lineHeight: '16px', color: COLORS.muted, mt: '2px' }}>
          {nameSource}
        </Typography>
      )}
    </Box>
  );
}

function recordColumns(match: PossibleMatch) {
  const category = categoryOf(match);
  const names = category === 'same_id_name' || category === 'id_conflict' || category === 'missing_id' ? diffSegments(match.records.map(recordName)) : undefined;
  const ids = category === 'id_conflict' ? diffSegments(match.records.map((r) => recordId(r, match.records))) : undefined;
  const dobs = category === 'same_id_dob' && match.records.every((r) => r.dob) ? diffSegments(match.records.map((r) => displayDob(r.dob ?? ''))) : undefined;
  return match.records.map((record, idx) => (
    <RecordColumn key={`${record.source.kind}:${record.source.uploadId ?? ''}:${idx}`} record={record} records={match.records} name={names?.[idx]} id={ids?.[idx]} dob={dobs?.[idx]} />
  ));
}

const ROW_SX = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: 2,
  py: 1.5,
  borderTop: `1px solid ${COLORS.hairlineSoft}`,
} as const;

function OpenRow({ match, busy, onAnswer }: { match: PossibleMatch; busy: boolean; onAnswer: (match: PossibleMatch, kind: MatchAnswerKind) => void }) {
  return (
    <Box data-testid="match-row" sx={ROW_SX}>
      {recordColumns(match)}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0, ml: 'auto' }}>
        <Box component="button" type="button" disabled={busy} onClick={() => onAnswer(match, 'keep_separate')} sx={{ ...TEXT_BUTTON_SX, border: 0, cursor: 'pointer' }}>
          Keep separate
        </Box>
        <Box
          component="button"
          type="button"
          disabled={busy}
          onClick={() => onAnswer(match, 'same_student')}
          sx={{ ...TEXT_BUTTON_SX, border: `1px solid ${COLORS.divider}`, color: COLORS.ink, cursor: 'pointer', '&:hover': { borderColor: COLORS.ink, bgcolor: 'transparent' } }}
        >
          Same student
        </Box>
      </Stack>
    </Box>
  );
}

function ReviewedRow({ match, busy, onChange }: { match: PossibleMatch; busy: boolean; onChange: (match: PossibleMatch) => void }) {
  return (
    <Box data-testid="reviewed-row" sx={ROW_SX}>
      {recordColumns(match)}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ flexShrink: 0, ml: 'auto' }}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: DATA_REVIEW.success, fontSize: 14, fontWeight: 700 }}>
          <CheckRoundedIcon sx={{ fontSize: 18 }} />
          <span>{match.answer?.kind === 'same_student' ? 'Same student' : 'Kept separate'}</span>
        </Stack>
        <Box component="button" type="button" disabled={busy} onClick={() => onChange(match)} sx={{ ...TEXT_BUTTON_SX, border: 0, cursor: 'pointer' }}>
          Change
        </Box>
      </Stack>
    </Box>
  );
}

function Progress({ done, total, pending }: { done: number; total: number; pending: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  return (
    <Box sx={{ minWidth: 260 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ gap: 2, mb: 0.75 }}>
        <Typography component="b" sx={{ fontSize: 15, lineHeight: '22px', fontWeight: 700, color: COLORS.ink }}>
          {done} of {total} reviewed
        </Typography>
        {pending > 0 && (
          <Typography component="span" sx={{ fontSize: 14, lineHeight: '20px', color: COLORS.muted }}>
            {heldOutShort(pending)}
          </Typography>
        )}
      </Stack>
      <Box role="progressbar" aria-valuenow={done} aria-valuemin={0} aria-valuemax={total} sx={{ height: 8, borderRadius: 4, bgcolor: '#E3E3E8', overflow: 'hidden' }}>
        <Box sx={{ width: `${pct}%`, height: '100%', bgcolor: COLORS.navy }} />
      </Box>
    </Box>
  );
}

export function MatchesPage() {
  const [state, setState] = useState<MatchesState | null>(null);
  const [failed, setFailed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [reviewedOpen, setReviewedOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    fetchMatches()
      .then((next) => {
        if (active) setState(next);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, []);

  const open = useMemo(() => (state ? openMatches(state.matches) : []), [state]);
  const groups = useMemo(() => (state ? groupOpenMatches(state.matches) : []), [state]);
  const reviewed = useMemo(() => (state ? reviewedMatches(state.matches) : []), [state]);
  const { hidden } = visibleMatches(open, expanded);
  const shownIds = useMemo(() => new Set(visibleMatches(open, expanded).shown.map((m) => m.id)), [open, expanded]);

  const run = async (id: string, call: () => Promise<MatchesState>, after?: (next: MatchesState) => void) => {
    if (busy !== null) return;
    setBusy(id);
    setError(null);
    try {
      const next = await call();
      setState(next);
      after?.(next);
    } catch (e) {
      setError(
        e instanceof MatchesRefreshError
          ? 'Your answer was saved, but the list could not be refreshed. Reload the page.'
          : 'That answer did not save. Try again.',
      );
    } finally {
      setBusy(null);
    }
  };

  const onAnswer = (match: PossibleMatch, kind: MatchAnswerKind) =>
    run(match.id, () => answerMatch(match.id, kind), (next) => setToast({ id: match.id, message: answerToast(kind, matchName(match), next.toReview) }));
  const onClear = (match: PossibleMatch) => run(match.id, () => clearMatchAnswer(match.id));
  const onUndo = () => {
    if (!toast) return;
    const id = toast.id;
    setToast(null);
    void run(id, () => clearMatchAnswer(id));
  };

  const total = state ? state.matches.length : 0;
  const done = state ? state.reviewed : 0;

  return (
    <Box sx={{ display: 'flex', minHeight: HEIGHT_BELOW_BARS, bgcolor: COLORS.bg }}>
      <title>Possible matches</title>
      <Sidebar sections={SIDEBAR_SECTIONS} activeId="data" />
      <Box component="main" sx={{ flex: 1, padding: '32px 24px' }}>
        <Stack spacing={3} sx={{ maxWidth: PAGE_MAX_WIDTH, mx: 'auto' }}>
          <Typography component="nav" aria-label="Breadcrumb" sx={{ fontSize: 14, lineHeight: '20px', color: COLORS.muted, display: 'flex', gap: 1 }}>
            <Box component={RouterLink} to="/data" sx={{ color: COLORS.navy, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              Data &amp; Integrations
            </Box>
            <span>/</span>
            <span aria-current="page">Possible matches</span>
          </Typography>
          <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'flex-end' }} sx={{ gap: 2 }}>
            <Typography component="h1" sx={{ fontSize: 28, lineHeight: '36px', fontWeight: 700, color: COLORS.ink }}>
              Possible matches
            </Typography>
            {state && total > 0 && <Progress done={done} total={total} pending={state.heldOutStudents} />}
          </Stack>
          {error && (
            <Typography role="alert" sx={{ fontSize: 15, lineHeight: '22px', color: COLORS.ink }}>
              {error}
            </Typography>
          )}
          {failed && <LoadFailure tokens={{ font: 'inherit', ink: ROSTER.ink, muted: ROSTER.muted }} />}
          {!failed && state === null && (
            <Box role="status" aria-label="Loading possible matches" sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: COLORS.muted }} />
            </Box>
          )}
          {state && open.length > 0 && (
            <Box component="section" sx={CARD_SX}>
              <Typography component="h2" sx={{ fontSize: 18, lineHeight: '26px', fontWeight: 700, color: COLORS.ink, mb: 1 }}>
                To review <Box component="span" sx={{ color: COLORS.muted, fontWeight: 500 }}>{open.length}</Box>
              </Typography>
              {groups.map((group) => {
                const rows = group.matches.filter((m) => shownIds.has(m.id));
                if (rows.length === 0) return null;
                return (
                  <Box key={group.category} sx={{ mt: 2 }}>
                    <Typography component="h3" sx={{ fontSize: 14, lineHeight: '20px', fontWeight: 700, color: COLORS.muted, mb: 0.5 }}>
                      {group.header}
                    </Typography>
                    {rows.map((match) => (
                      <OpenRow key={match.id} match={match} busy={busy !== null} onAnswer={onAnswer} />
                    ))}
                  </Box>
                );
              })}
              {(hidden > 0 || (expanded && open.length > 3)) && (
                <Box component="button" type="button" onClick={() => setExpanded((v) => !v)} sx={{ ...TEXT_BUTTON_SX, border: 0, cursor: 'pointer', mt: 1.5, px: 0 }}>
                  {hidden > 0 ? `Show ${hidden} more` : 'Show fewer'}
                </Box>
              )}
            </Box>
          )}
          {state && total > 0 && open.length === 0 && (
            <Box component="section" sx={{ ...CARD_SX, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
              <Typography component="h2" sx={{ fontSize: 18, lineHeight: '26px', fontWeight: 700, color: COLORS.ink, flex: '1 1 auto' }}>
                Every match has been reviewed
              </Typography>
              <Box component={RouterLink} to="/data" sx={PRIMARY_BUTTON_SX}>
                Back to Data &amp; Integrations
              </Box>
            </Box>
          )}
          {state && total === 0 && (
            <Box component="section" sx={CARD_SX}>
              <Typography component="h2" sx={{ fontSize: 18, lineHeight: '26px', fontWeight: 700, color: COLORS.ink }}>
                No possible matches to review
              </Typography>
              <Typography sx={{ fontSize: 15, lineHeight: '22px', color: COLORS.muted, mt: 0.5 }}>
                Matching runs after every upload and every SIS sync.
              </Typography>
            </Box>
          )}
          {reviewed.length > 0 && (
            <Box component="section" sx={CARD_SX}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography component="h2" sx={{ fontSize: 18, lineHeight: '26px', fontWeight: 700, color: COLORS.ink }}>
                  Reviewed <Box component="span" sx={{ color: COLORS.muted, fontWeight: 500 }}>{reviewed.length}</Box>
                </Typography>
                <Button
                  onClick={() => setReviewedOpen((v) => !v)}
                  aria-expanded={reviewedOpen}
                  endIcon={<ExpandMoreIcon sx={{ transform: reviewedOpen ? 'rotate(180deg)' : 'none' }} />}
                  sx={{ textTransform: 'none', fontWeight: 600, color: COLORS.navy }}
                >
                  {reviewedOpen ? 'Hide' : 'Show'}
                </Button>
              </Stack>
              {reviewedOpen && (
                <Box sx={{ mt: 1 }}>
                  {reviewed.map((match) => (
                    <ReviewedRow key={match.id} match={match} busy={busy !== null} onChange={onClear} />
                  ))}
                </Box>
              )}
            </Box>
          )}
        </Stack>
      </Box>
      {toast && (
        <Snackbar
          key={toast.id}
          open
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          autoHideDuration={8000}
          onClose={(_, reason) => reason !== 'clickaway' && setToast(null)}
        >
          <SnackbarContent
            role="status"
            data-testid="answer-toast"
            message={toast.message}
            sx={{ bgcolor: TOAST_BG, color: '#FFFFFF', borderRadius: '4px' }}
            action={
              <>
                <Button onClick={onUndo} disabled={busy !== null} sx={{ textTransform: 'none', fontWeight: 600, color: '#FFFFFF', '&:disabled': { color: 'rgba(255,255,255,0.5)' } }}>
                  Undo
                </Button>
                <IconButton aria-label="Dismiss" onClick={() => setToast(null)} sx={{ color: '#FFFFFF' }}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </>
            }
          />
        </Snackbar>
      )}
    </Box>
  );
}
